import { useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import Seo, { breadcrumbs } from "../components/Seo";
import { SectionHeader, Hero, Disclaimer } from "../components/UI";
import { siteConfig, disclaimers, fmtMoney } from "../config";

/* ---------- Privacy / Terms / Accessibility ---------- */
export function Privacy() {
  return (
    <main id="main-content">
      <Seo title="Privacy Policy | Scott Sells Chevys" description="Privacy policy for the Scott Sells Chevys website." canonicalPath="/privacy-policy" />
      <Hero title="Privacy Policy" />
      <section className="ssc-container ssc-section prose max-w-3xl">
        <p className="text-[var(--ssc-ink-2)]">This website collects only the information you choose to submit through forms — for example, your name, contact info, vehicle interest, and any details you share about your trade. Submissions are sent to Scott Hall to follow up with you about your request. We do not sell your personal information.</p>
        <h3 className="font-extrabold mt-6 text-[var(--ssc-navy)]">What we collect</h3>
        <ul className="list-disc pl-6 text-[var(--ssc-ink-2)]">
          <li>Form submissions (name, phone, email, message, vehicle context)</li>
          <li>Basic technical information (IP address, page URL, browser) for spam protection and analytics</li>
        </ul>
        <h3 className="font-extrabold mt-6 text-[var(--ssc-navy)]">How we use it</h3>
        <p className="text-[var(--ssc-ink-2)]">To respond to your inquiry, follow up about your vehicle interest, and improve the website experience. Communications follow your stated preferred contact method.</p>
        <h3 className="font-extrabold mt-6 text-[var(--ssc-navy)]">Cookies & analytics</h3>
        <p className="text-[var(--ssc-ink-2)]">If enabled, this site may use analytics tools (Google Analytics, Meta Pixel) using the IDs configured in the site config. You can disable cookies in your browser settings.</p>
        <h3 className="font-extrabold mt-6 text-[var(--ssc-navy)]">Contact</h3>
        <p className="text-[var(--ssc-ink-2)]">Email <a href={siteConfig.scott.emailMailto} className="underline">{siteConfig.scott.email}</a> with any privacy questions.</p>
      </section>
    </main>
  );
}

export function Terms() {
  return (
    <main id="main-content">
      <Seo title="Terms of Use | Scott Sells Chevys" description="Terms of use for the Scott Sells Chevys website." canonicalPath="/terms-of-use" />
      <Hero title="Terms of Use" />
      <section className="ssc-container ssc-section max-w-3xl space-y-4 text-[var(--ssc-ink-2)]">
        <p>This website is a personal sales and follow-up tool. Information shown — including inventory, pricing, mileage, photos, availability, payments, rebates, and incentives — is provided for convenience only and is subject to change.</p>
        <p>{disclaimers.sitewide}</p>
        <p>By using the site, you agree to use it only for lawful purposes, to provide accurate information when contacting Scott, and to confirm any final pricing, vehicle availability, financing, and trade details with the dealership before making a purchase.</p>
      </section>
    </main>
  );
}

export function Accessibility() {
  return (
    <main id="main-content">
      <Seo title="Accessibility | Scott Sells Chevys" description="Accessibility statement for the Scott Sells Chevys website." canonicalPath="/accessibility" />
      <Hero title="Accessibility" />
      <section className="ssc-container ssc-section max-w-3xl space-y-4 text-[var(--ssc-ink-2)]">
        <p>We aim to make the site usable for everyone, including drivers using assistive technology. We follow common accessibility practices: semantic HTML, keyboard navigation, focus states, alt text on imagery, accessible buttons, and a mobile-first responsive layout.</p>
        <p>If you encounter an accessibility issue, please contact Scott at <a className="underline" href={siteConfig.scott.emailMailto}>{siteConfig.scott.email}</a> and we'll work to resolve it.</p>
      </section>
    </main>
  );
}

/* ---------- Sitemap ---------- */
const PAGES = [
  ["Home", "/"],
  ["New Vehicles", "/new-vehicles"],
  ["New Cars", "/new-vehicles/cars"],
  ["New SUVs", "/new-vehicles/suvs"],
  ["New Trucks", "/new-vehicles/trucks"],
  ["New EVs", "/new-vehicles/evs"],
  ["New Vehicle Specials", "/new-vehicles/specials"],
  ["New Lease Options", "/new-vehicles/lease-options"],
  ["New Silverado", "/new-vehicles/silverado"],
  ["New Colorado", "/new-vehicles/colorado"],
  ["New Equinox", "/new-vehicles/equinox"],
  ["New Traverse", "/new-vehicles/traverse"],
  ["New Blazer", "/new-vehicles/blazer"],
  ["New Trax", "/new-vehicles/trax"],
  ["New Tahoe", "/new-vehicles/tahoe"],
  ["New Suburban", "/new-vehicles/suburban"],
  ["Used Vehicles", "/used-vehicles"],
  ["Used Cars", "/used-vehicles/cars"],
  ["Used SUVs", "/used-vehicles/suvs"],
  ["Used Trucks", "/used-vehicles/trucks"],
  ["Used EVs", "/used-vehicles/evs"],
  ["Used Under $20,000", "/used-vehicles/under-20000"],
  ["Used Under $25,000", "/used-vehicles/under-25000"],
  ["Low Mileage Used", "/used-vehicles/low-mileage"],
  ["Fresh Trades", "/used-vehicles/fresh-trades"],
  ["One-Owner Used", "/used-vehicles/one-owner"],
  ["Used AWD & 4x4", "/used-vehicles/awd-4x4"],
  ["Inventory Search", "/inventory"],
  ["Shop by Need", "/shop-by-need"],
  ["Trucks (Need)", "/shop-by-need/trucks"],
  ["SUVs (Need)", "/shop-by-need/suvs"],
  ["EVs (Need)", "/shop-by-need/evs"],
  ["AWD/4x4 (Need)", "/shop-by-need/awd-4x4"],
  ["Family Vehicles", "/shop-by-need/family-vehicles"],
  ["Low Payment Options", "/shop-by-need/payment"],
  ["First-Time Buyer Picks", "/shop-by-need/first-time-buyer"],
  ["Work Vehicles", "/shop-by-need/work-vehicles"],
  ["Commuter Vehicles", "/shop-by-need/commuter-vehicles"],
  ["Finance & Trade", "/finance-trade"],
  ["Payment Help", "/payment-help"],
  ["Value My Trade", "/value-my-trade"],
  ["Build My Own Deal", "/scotts-deal-desk"],
  ["Lease Help", "/lease-help"],
  ["Finance Help", "/finance-help"],
  ["Trade-In Checklist", "/trade-in-checklist"],
  ["Payment Calculator", "/payment-calculator"],
  ["Resources", "/resources"],
  ["EV Help", "/ev-help"],
  ["Vehicle Alerts", "/vehicle-alerts"],
  ["Reviews", "/reviews"],
  ["FAQ", "/faq"],
  ["Service Areas", "/service-areas"],
  ["Car Buying Tips", "/car-buying-tips"],
  ["New vs Used Guide", "/new-vs-used"],
  ["About Scott", "/about-scott"],
  ["Contact", "/contact"],
  ["Compare Vehicles", "/compare"],
  ["Saved Vehicles", "/saved-vehicles"],
  ["Schedule Test Drive", "/schedule-test-drive"],
  ["Find Me a Vehicle", "/find-me-a-vehicle"],
  ["Privacy Policy", "/privacy-policy"],
  ["Terms of Use", "/terms-of-use"],
  ["Accessibility", "/accessibility"],
];

export function Sitemap() {
  return (
    <main id="main-content">
      <Seo title="Sitemap | Scott Sells Chevys" description="Full sitemap for Scott Sells Chevys." canonicalPath="/sitemap" />
      <Hero title="Sitemap" />
      <section className="ssc-section">
        <div className="ssc-container grid md:grid-cols-2 lg:grid-cols-3 gap-2">
          {PAGES.map(([t, h]) => <Link key={h} to={h} className="ssc-card text-[var(--ssc-navy)] font-semibold">{t}</Link>)}
          {siteConfig.serviceAreaCities.map((c) => (
            <Link key={c} to={`/service-areas/${c.toLowerCase().replace(/\s+/g, "-")}`} className="ssc-card text-[var(--ssc-navy)] font-semibold">Service Area: {c}</Link>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ---------- 404 ---------- */
export function NotFound() {
  const { pathname } = useLocation();
  // Unknown /chevy-sales-* slugs redirect to home instead of showing 404,
  // so search engines don't index fake-city dead pages.
  if (pathname.startsWith("/chevy-sales-")) {
    return <Navigate to="/" replace />;
  }
  return (
    <main id="main-content" className="ssc-container ssc-section text-center">
      <Seo title="Page Not Found | Scott Sells Chevys" description="The page you requested could not be found." canonicalPath="/404" />
      <h1 className="text-3xl md:text-5xl font-extrabold text-[var(--ssc-navy)]">This page took a wrong turn.</h1>
      <p className="mt-3 text-[var(--ssc-ink-2)]">The vehicle or page you're looking for may have moved, sold, or changed.</p>
      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy">Search New Vehicles</Link>
        <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline">Search Used Vehicles</Link>
        <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-red">Find Me a Vehicle</Link>
        <Link to="/scotts-deal-desk" className="ssc-btn ssc-btn-outline">Build My Own Deal</Link>
        <Link to="/contact" className="ssc-btn ssc-btn-primary">Contact Scott</Link>
      </div>
    </main>
  );
}

/* ---------- Payment Calculator ---------- */
export function PaymentCalculator() {
  const [price, setPrice] = useState(35000);
  const [down, setDown] = useState(2000);
  const [trade, setTrade] = useState(0);
  const [rate, setRate] = useState(7.5);
  const [term, setTerm] = useState(60);

  const principal = Math.max(0, Number(price) - Number(down) - Number(trade));
  const r = (Number(rate) / 100) / 12;
  const n = Number(term);
  const payment = r > 0 ? (principal * r) / (1 - Math.pow(1 + r, -n)) : principal / n;

  return (
    <main id="main-content">
      <Seo title="Payment Calculator | Scott Sells Chevys" description="Estimate a vehicle payment to start the conversation." canonicalPath="/payment-calculator" />
      <Hero title="Payment Calculator" subtitle="A quick way to estimate a payment direction." />
      <section className="ssc-section">
        <div className="ssc-container grid md:grid-cols-2 gap-6 max-w-4xl">
          <div className="ssc-card space-y-3">
            {[
              ["Vehicle Price", price, setPrice],
              ["Down Payment", down, setDown],
              ["Trade Value", trade, setTrade],
              ["Interest Rate (%)", rate, setRate],
              ["Term (months)", term, setTerm],
            ].map(([label, val, set]) => (
              <div key={label}>
                <label className="ssc-label">{label}</label>
                <input type="number" inputMode="decimal" className="ssc-input" value={val} onChange={(e) => set(e.target.value)} data-testid={`pc-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              </div>
            ))}
          </div>
          <div className="ssc-card-dark flex flex-col">
            <div className="text-[var(--ssc-silver)] text-sm uppercase tracking-wider">Estimated monthly payment</div>
            <div className="text-5xl font-extrabold mt-2" data-testid="pc-result">{isFinite(payment) ? fmtMoney(payment) : "—"}</div>
            <p className="text-[var(--ssc-silver)] text-sm mt-3">For planning only. Excludes taxes, fees, and lender programs.</p>
            <div className="mt-auto pt-4 flex flex-wrap gap-2">
              <Link to="/payment-help" className="ssc-btn ssc-btn-primary">Get Payment Help</Link>
              <a href={siteConfig.links.financeApply} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-ghost">Apply for Financing</a>
            </div>
            <div className="mt-4"><Disclaimer text={disclaimers.payment} /></div>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ---------- Trade-In Checklist ---------- */
export function TradeInChecklist() {
  const items = [
    "Title or payoff information",
    "Driver's license",
    "Vehicle registration",
    "All keys and key fobs",
    "Owner's manual (if available)",
    "Service records (if available)",
    "Any aftermarket accessory documentation",
  ];
  return (
    <main id="main-content">
      <Seo title="Trade-In Checklist | Scott Sells Chevys" description="What to bring when trading in your vehicle." canonicalPath="/trade-in-checklist" />
      <Hero title="Trade-In Checklist" subtitle="A quick list of what helps the trade go smoothly." />
      <section className="ssc-section">
        <div className="ssc-container max-w-3xl">
          <ul className="ssc-card space-y-2 text-[var(--ssc-ink-2)] list-disc pl-5">
            {items.map((i) => <li key={i}>{i}</li>)}
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/value-my-trade" className="ssc-btn ssc-btn-primary">Value My Trade</Link>
            <Link to="/contact" className="ssc-btn ssc-btn-outline">Ask Scott a Question</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ---------- Lease / Finance / NewVsUsed / Car Buying Tips ---------- */
export function LeaseHelp() {
  return (
    <main id="main-content">
      <Seo title="Lease Help | Scott Sells Chevys" description="When leasing makes sense, mileage limits, end-of-lease options, and more." canonicalPath="/lease-help" />
      <Hero title="Lease Help" subtitle="Lower payments and shorter commitments — but it's not for everyone. Here's how to think about it." />
      <section className="ssc-section">
        <div className="ssc-container grid md:grid-cols-2 gap-6 max-w-5xl">
          <div className="ssc-card">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">When leasing makes sense</h2>
            <ul className="mt-2 list-disc pl-5 text-[var(--ssc-ink-2)] space-y-1">
              <li>You prefer a newer vehicle every 2–3 years</li>
              <li>You want a lower monthly payment</li>
              <li>You drive predictable miles per year</li>
              <li>You like factory warranty coverage</li>
            </ul>
          </div>
          <div className="ssc-card">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">Things to think about</h2>
            <ul className="mt-2 list-disc pl-5 text-[var(--ssc-ink-2)] space-y-1">
              <li>Mileage limits and overage fees</li>
              <li>Wear and tear at lease end</li>
              <li>End-of-lease options (return, buy, trade)</li>
              <li>Incentives that may favor lease or finance</li>
            </ul>
          </div>
        </div>
        <div className="ssc-container mt-6 flex flex-wrap gap-3">
          <Link to="/new-vehicles/lease-options" className="ssc-btn ssc-btn-navy">See Lease Options</Link>
          <Link to="/payment-help" className="ssc-btn ssc-btn-outline">Payment Help</Link>
          <a href={siteConfig.links.financeApply} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-primary">Apply for Financing</a>
        </div>
      </section>
    </main>
  );
}

export function FinanceHelp() {
  return (
    <main id="main-content">
      <Seo title="Finance Help | Scott Sells Chevys" description="How financing works, credit basics, and how to apply through the official Byers Chevrolet link." canonicalPath="/finance-help" />
      <Hero title="Finance Help" subtitle="Buying a vehicle? Here's how to think about your finance options." />
      <section className="ssc-section">
        <div className="ssc-container max-w-3xl space-y-4 text-[var(--ssc-ink-2)]">
          <p>Financing typically depends on credit history, income, down payment, vehicle price, trade value, and lender programs available at the time. Scott can help you plan around your goal — and the actual approval happens through the dealership and lenders.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Pre-qualification helps you understand a range early.</li>
            <li>Down payment and trade reduce the amount financed.</li>
            <li>Term length affects your monthly payment and total interest.</li>
          </ul>
        </div>
        <div className="ssc-container mt-6 flex flex-wrap gap-3">
          <a href={siteConfig.links.financeApply} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-primary">Apply for Financing</a>
          <Link to="/payment-help" className="ssc-btn ssc-btn-outline">Payment Help</Link>
          <Link to="/scotts-deal-desk" className="ssc-btn ssc-btn-navy">Build My Own Deal</Link>
        </div>
        <div className="ssc-container mt-6"><Disclaimer text={disclaimers.payment} /></div>
      </section>
    </main>
  );
}

export function NewVsUsed() {
  return (
    <main id="main-content">
      <Seo title="New vs Used Guide | Scott Sells Chevys" description="How to think about new vs used vehicles — pros, cons, and how Scott helps you compare." canonicalPath="/new-vs-used" />
      <Hero title="New vs Used: How to Think About It" subtitle="Both can be great choices. Here's how Scott helps you decide." />
      <section className="ssc-section">
        <div className="ssc-container grid md:grid-cols-2 gap-6 max-w-5xl">
          <div className="ssc-card">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">New is great when…</h2>
            <ul className="mt-2 list-disc pl-5 text-[var(--ssc-ink-2)] space-y-1">
              <li>You want the latest tech and safety features</li>
              <li>You like factory warranty coverage</li>
              <li>You're considering lease incentives</li>
              <li>You want to spec a vehicle to your needs</li>
            </ul>
          </div>
          <div className="ssc-card">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">Used is great when…</h2>
            <ul className="mt-2 list-disc pl-5 text-[var(--ssc-ink-2)] space-y-1">
              <li>You want to maximize value per dollar</li>
              <li>You want a lower price and/or payment</li>
              <li>You like the model year that's already out</li>
              <li>You want a one-owner or fresh trade</li>
            </ul>
          </div>
        </div>
        <div className="ssc-container mt-6 flex flex-wrap gap-3">
          <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy">Shop New</Link>
          <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline">Shop Used</Link>
          <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-red">Find Me a Vehicle</Link>
        </div>
      </section>
    </main>
  );
}

export function CarBuyingTips() {
  const tips = [
    ["Know your needs first", "Truck vs SUV vs car, daily miles, family needs, weather, and parking all matter."],
    ["Plan around payment, not just price", "Trade, down payment, term, and rate all shape the final number."],
    ["Get a trade reality check", "Run the dealer-grade trade valuation tool and pair it with an in-person appraisal for a realistic starting point."],
    ["Test drive what you're considering", "Even a 10-minute drive can confirm or eliminate a model."],
    ["Don't skip the financing prep", "Pre-qualification helps you compare options with confidence."],
    ["Ask about accessories upfront", "Easier and often cheaper than adding later."],
  ];
  return (
    <main id="main-content">
      <Seo title="Car Buying Tips | Scott Sells Chevys" description="Practical, no-pressure tips for shopping new and used vehicles." canonicalPath="/car-buying-tips" />
      <Hero title="Car Buying Tips" subtitle="A short list of practical, no-pressure tips." />
      <section className="ssc-section">
        <div className="ssc-container grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          {tips.map(([t, p]) => (
            <div key={t} className="ssc-card">
              <div className="font-extrabold text-[var(--ssc-navy)]">{t}</div>
              <p className="text-[var(--ssc-ink-2)] mt-2 text-sm">{p}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
